import { redirect } from 'next/navigation'

import { Query } from '@/shared-gui-components/table'
import { Nullable } from '@/shared-types/types'
import { TApiError } from '@/shared-types/types/api'

type TMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export class ApiError extends Error {
    constructor(
        public status: number,
        public body: TApiError
    ) {
        super(body.message)
    }
}

const isBrowser = typeof window !== 'undefined'

const apiFetchUncoalesced = async <T>(path: string, method: TMethod, body?: unknown): Promise<T> => {
    const headers: Record<string, string> = {}
    if (body !== undefined)
        headers['Content-Type'] = 'application/json'

    // The browser sends the session cookie itself (credentials: 'include'); a server component forwards the
    // cookies of the incoming request.
    if (!isBrowser) {
        const { cookies } = await import('next/headers')
        const cookieHeader = (await cookies()).toString()
        if (cookieHeader)
            headers.cookie = cookieHeader
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'include',
        cache: 'no-store',
    })

    if (response.status == 401) {
        if (!isBrowser)
            redirect('/login')

        if (!window.location.pathname.startsWith('/login'))
            window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`
    }

    if (!response.ok) {
        let error: TApiError = { error: 'unknown', message: response.statusText }

        try {
            error = await response.json()
        }
        catch {
            // non-JSON error body
        }

        throw new ApiError(response.status, error)
    }

    return response.json() as Promise<T>
}

const inFlightRequests = new Map<string, Promise<unknown>>()

/**
 * Call of the backend from the browser (directly, CORS with the session cookie) or from a server component.
 * Concurrent GETs of the same path share one request, in the browser only: a module-level map on the server is
 * shared by every request, which would hand one user's response to another.
 */
export const apiFetch = <T>(path: string, method: TMethod = 'GET', body?: unknown): Promise<T> => {
    if (!isBrowser || method != 'GET' || body !== undefined)
        return apiFetchUncoalesced<T>(path, method, body)

    const existing = inFlightRequests.get(path)
    if (existing)
        return existing as Promise<T>

    const request = apiFetchUncoalesced<T>(path, method).finally(() => inFlightRequests.delete(path))
    inFlightRequests.set(path, request)

    return request
}

/** Appends the shared table query (page, pageSize, filters, sorter) to a path. */
export const withQuery = (path: string, query?: Query) => {
    const qs = Query.build(query)

    return qs ? `${path}?${qs}` : path
}

/** For server components: null instead of an error on 403 / 404 (no permission, not found); 401 redirects to /login. */
export const orNull = async <T>(request: Promise<T>): Promise<Nullable<T>> => {
    try {
        return await request
    }
    catch (err) {
        if (err instanceof ApiError && [403, 404].includes(err.status))
            return null

        throw err
    }
}

type TId = number | string

/** Response and input types of a resource; a method whose type is not given returns never. */
type TResource = {
    get?: unknown
    getOne?: unknown
    input?: unknown
    post?: unknown
    put?: unknown
    delete?: unknown
}

type TOf<R extends TResource, K extends keyof TResource> = K extends keyof R ? R[K] : never

/**
 * REST methods of one backend resource, like createAPI in bigcloud:
 * get = GET /route?query, getOne = GET /route/id, post = POST /route, put = PUT /route/id, delete = DELETE /route/id.
 */
const createAPI = <R extends TResource>(route: string) => ({
    get: (query?: Query) => apiFetch<TOf<R, 'get'>>(withQuery(route, query)),
    getOne: (id: TId) => apiFetch<TOf<R, 'getOne'>>(`${route}/${id}`),
    post: (input: TOf<R, 'input'>) => apiFetch<TOf<R, 'post'>>(route, 'POST', input),
    put: (id: TId, input: TOf<R, 'input'>) => apiFetch<TOf<R, 'put'>>(`${route}/${id}`, 'PUT', input),
    delete: (id: TId) => apiFetch<TOf<R, 'delete'>>(`${route}/${id}`, 'DELETE'),
})

export default createAPI
